// Pure utility functions for anomaly description handling
// These functions are extracted for better testability and maintainability
import { getCentroidForGeometry } from '../Adjuster.utils';
/**
 * Validates that the required refs are available and have current values
 * @param {Object} mapViewerRef - Reference to the map viewer component
 * @param {Object} containerRef - Reference to the container element
 * @returns {boolean} True if both refs are valid
 */
export const validateRefs = (mapViewerRef, containerRef) => !!(mapViewerRef && mapViewerRef.current && containerRef && containerRef.current);

/**
 * Finds a target anomaly by its ID from an array of anomalies
 * @param {Array} anomalies - Array of anomaly objects
 * @param {string} anomalyId - ID of the anomaly to find
 * @returns {Object|undefined} The found anomaly object or undefined
 */
export const findTargetAnomaly = (anomalies, anomalyId) => anomalies.find((anomaly) => anomaly.properties && anomaly.properties.anomalyId === anomalyId);

/**
 * Validates that an anomaly has valid geometry data
 * @param {Object} targetAnomaly - The anomaly object to validate
 * @returns {boolean} True if the anomaly has valid geometry
 */
export const validateAnomalyGeometry = (targetAnomaly) => !!(targetAnomaly && targetAnomaly.geometries && targetAnomaly.geometries[0]);

/**
 * Extracts and validates coordinates from an anomaly object
 * @param {Object} targetAnomaly - The anomaly object containing geometry
 * @returns {Object} Object with valid flag and coordinates (lon, lat) or error info
 */
export const extractCoordinates = (targetAnomaly) => {
  try {
    const geometry = targetAnomaly && targetAnomaly.geometries && targetAnomaly.geometries[0];
    if (!geometry || !geometry.coordinates) {
      return {
        valid: false,
        lon: 0,
        lat: 0,
      };
    }

    // If it's already a Point type with exactly 2 coordinates, use them directly
    if (geometry.type === 'Point' && geometry.coordinates.length === 2) {
      return {
        valid: true,
        lon: geometry.coordinates[0],
        lat: geometry.coordinates[1],
      };
    }

    // For Polygon type, get centroid
    if (geometry.type === 'Polygon' && geometry.coordinates.length > 0) {
      const centroid = getCentroidForGeometry(geometry);
      if (centroid && centroid.geometry && centroid.geometry.coordinates && centroid.geometry.coordinates.length === 2) {
        return {
          valid: true,
          lon: centroid.geometry.coordinates[0],
          lat: centroid.geometry.coordinates[1],
        };
      }
    }

    return {
      valid: false,
      lon: 0,
      lat: 0,
    };
  } catch (error) {
    return {
      valid: false,
      lon: 0,
      lat: 0,
    };
  }
};

/**
 * Calculates the appropriate zoom level for anomaly focus
 * @param {number} testSquareZoomLevel - Base zoom level for test square
 * @returns {number} Calculated zoom level for anomaly
 */
export const calculateAnomalyZoomLevel = (testSquareZoomLevel) => testSquareZoomLevel + 1;

/**
 * Calculates smart positioning for the description panel
 * @param {Object} pixelPosition - Pixel position {x, y} of the anomaly
 * @param {Object} containerRef - Reference to the container element
 * @param {Object} panelConstants - Panel dimensions {WIDTH, HEIGHT}
 * @param {Function} isCompleteVisibleH - Function to check horizontal visibility
 * @param {Function} isCompleteVisibleV - Function to check vertical visibility
 * @returns {Object} Calculated position {top, left, panelWidth, panelHeight}
 */
export const calculateSmartPosition = (
  pixelPosition,
  containerRef,
  panelConstants,
  isCompleteVisibleH,
  isCompleteVisibleV,
) => {
  const { x, y } = pixelPosition;
  const { WIDTH: panelWidth, HEIGHT: panelHeight } = panelConstants;
  const offset = 8;

  // Check if panel would be visible horizontally and vertically
  const isDescPanelVisibleH = isCompleteVisibleH(x + offset, containerRef);
  const isDescPanelVisibleV = isCompleteVisibleV(y + offset, containerRef);

  // Calculate smart positioning
  const top = y + (isDescPanelVisibleV ? offset : (-panelHeight));
  const left = x + (isDescPanelVisibleH ? offset : (-panelWidth));

  return {
    top,
    left,
    panelWidth,
    panelHeight,
  };
};

/**
 * Clamps panel position to stay within container boundaries
 * @param {Object} position - Calculated position {top, left}
 * @param {Object} containerDimensions - Container dimensions {offsetWidth, offsetHeight}
 * @param {Object} panelDimensions - Panel dimensions {panelWidth, panelHeight}
 * @returns {Object} Clamped position {top, left}
 */
export const clampPositionToContainer = (position, containerDimensions, panelDimensions) => {
  const { top, left } = position;
  const { offsetWidth, offsetHeight } = containerDimensions;
  const { panelWidth, panelHeight } = panelDimensions;

  let clampedTop = top;
  let clampedLeft = left;

  // Clamp to container boundaries
  if (clampedTop < 0) clampedTop = 0;
  if (clampedLeft < 0) clampedLeft = 0;
  if (clampedTop > (offsetHeight - panelHeight)) {
    clampedTop = offsetHeight - panelHeight;
  }
  if (clampedLeft > (offsetWidth - panelWidth)) {
    clampedLeft = offsetWidth - panelWidth;
  }

  return {
    top: clampedTop,
    left: clampedLeft,
  };
};

/**
 * Creates the easeTo animation configuration for map panning
 * @param {number} lon - Longitude coordinate
 * @param {number} lat - Latitude coordinate
 * @param {number} zoomLevel - Target zoom level
 * @param {number} duration - Animation duration in milliseconds
 * @returns {Object} EaseTo configuration object
 */
export const createEaseToConfig = (lon, lat, zoomLevel, duration = 1000) => ({
  center: [lon, lat],
  zoom: zoomLevel,
  duration,
});

/**
 * Creates the final panel position object for state update
 * @param {number} top - Top position
 * @param {number} left - Left position
 * @returns {Object} Position object for state
 */
export const createPanelPosition = (top, left) => ({ top, left });
